import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, Cell,
  XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine
} from "recharts";

// Australia's grid — emissions vs electricity generated (Jun 2006 → Jun 2025)
// Source: DCCEEW; Open Electricity
const energyData = [
  { year: "'06", emissions: 201.2, electricity: 202.29 },
  { year: "'07", emissions: 204.0, electricity: 204.97 },
  { year: "'08", emissions: 205.8, electricity: 206.37 },
  { year: "'09", emissions: 211.7, electricity: 208.28 },
  { year: "'10", emissions: 205.0, electricity: 209.51 },
  { year: "'11", emissions: 198.3, electricity: 207.22 },
  { year: "'12", emissions: 199.0, electricity: 203.20 },
  { year: "'13", emissions: 187.0, electricity: 198.04 },
  { year: "'14", emissions: 180.8, electricity: 202.92 },
  { year: "'15", emissions: 188.9, electricity: 211.28 },
  { year: "'16", emissions: 194.8, electricity: 223.30 },
  { year: "'17", emissions: 189.7, electricity: 221.46 },
  { year: "'18", emissions: 183.3, electricity: 222.22 },
  { year: "'19", emissions: 179.1, electricity: 222.97 },
  { year: "'20", emissions: 171.9, electricity: 223.08 },
  { year: "'21", emissions: 163.2, electricity: 222.21 },
  { year: "'22", emissions: 156.5, electricity: 226.00 },
  { year: "'23", emissions: 152.6, electricity: 228.19 },
  { year: "'24", emissions: 152.6, electricity: 232.66 },
  { year: "'25", emissions: 147.6, electricity: 236.33 },
];

const barData = [
  { label: "Petrol", v: 58 },
  { label: "Diesel", v: 34 },
  { label: "Hybrid", v: 18 },
  { label: "EV", v: 11 },
  { label: "PHEV", v: 4 },
];

const progressData = Array.from({ length: 30 }, (_, i) => ({
  t: i,
  v: 648 - i * 3.2 + Math.cos(i / 3) * 6,
}));

export default function Hero() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 noise"
    >
      <div className="absolute inset-0 grid-bg opacity-70 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        {/* Status bar */}
        <div className="flex flex-wrap items-center gap-4 mb-10">
          <div className="overline inline-flex items-center gap-2" data-testid="hero-eyebrow">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 bg-[#22C55E] rounded-full anim-blink" />
              <span className="w-2 h-2 bg-[#22C55E] rounded-full" />
            </span>
            Australian Fact Tank — Studio division
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: headline */}
          <div className="lg:col-span-7 anim-fade-up">
            <h1
              className="font-display font-black text-[3rem] sm:text-6xl lg:text-[5.75rem] leading-[0.95] tracking-[-0.04em] mb-8"
              data-testid="hero-headline"
            >
              Build your{" "}
              <span className="relative inline-block">
                <span className="relative z-10">case</span>
                <span className="absolute -bottom-1 left-0 right-0 h-4 bg-[#FDE047] -z-0" />
              </span>
              <br />
              with data.
            </h1>

            <p className="text-lg md:text-xl text-[#4B5563] max-w-xl leading-relaxed mb-10" data-testid="hero-sub">
              Custom tools and content that make evidence easier to understand and harder to ignore.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                onClick={() => window.gtag?.('event', 'cta_click', { location: 'hero' })}
                className="group bg-[#0A0A0A] text-white px-7 py-4 font-medium inline-flex items-center gap-2 hover:bg-[#0052FF] transition-colors"
              >
                Start a project
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#approach"
                data-testid="hero-secondary-cta"
                className="border-2 border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                See our services
              </a>
            </div>

            {/* Stats strip */}
            <div className="mt-14 grid grid-cols-3 gap-4 md:gap-8 border-t border-black/10 pt-8">
              <StatBlock value="10M+" label="Australians reached" testid="stat-aus" />
              <StatBlock value="500M+" label="Dashboard views" testid="stat-views" />
              <StatBlock value="2×" label="OAM awards, founders" testid="stat-oam" />
            </div>
          </div>

          {/* Right: chart stack */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-1 gap-4">
              {/* Hero chart 1 */}
              <div className="bg-white brutal p-5" data-testid="hero-chart-1">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <p className="overline">Hero chart</p>
                    <h3 className="font-display font-bold text-base">Electricity up, emissions down</h3>
                  </div>
                  <span className="font-mono text-xs bg-[#FDE047] px-2 py-0.5">2006–25</span>
                </div>
                <div className="h-[160px] -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={energyData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0052FF" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#0052FF" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="year" hide />
                      <YAxis hide domain={[140, 240]} />
                      <Tooltip
                        contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 0, fontSize: 12 }}
                        labelFormatter={(y) => `Jun ${y.replace("'", "20")}`}
                        formatter={(v, name) => {
                          if (name === "electricity") return [`${v.toFixed(1)} GWh`, "Electricity"];
                          if (name === "emissions") return [`${v.toFixed(1)} Mt CO₂e`, "Emissions"];
                          return [v, name];
                        }}
                      />
                      <Area type="monotone" dataKey="electricity" stroke="#0052FF" strokeWidth={2.5} fill="url(#g1)" />
                      <Line type="monotone" dataKey="emissions" stroke="#0A0A0A" strokeWidth={1.5} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-1">
                  <Legend color="#0052FF" label="Electricity (GWh)" />
                  <Legend color="#0A0A0A" label="Emissions (Mt CO₂e)" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Bar chart */}
                <div className="bg-white brutal p-4" data-testid="hero-chart-2">
                  <p className="overline text-[0.65rem]">Breakdown</p>
                  <h4 className="font-display font-bold text-sm mb-2">Vehicle sales</h4>
                  <div className="h-[96px] -mx-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={barData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <XAxis dataKey="label" hide />
                        <YAxis hide />
                        <Tooltip contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} />
                        <Bar dataKey="v">
                          {barData.map((d) => (
                            <Cell key={d.label} fill={d.label === "EV" ? "#0052FF" : "#0A0A0A"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="font-mono text-xs text-[#6B7280]">EV share</span>
                    <span className="font-display font-bold text-lg">11%</span>
                  </div>
                </div>

                {/* Progress chart */}
                <div className="bg-[#0A0A0A] text-white p-4 brutal-blue" data-testid="hero-chart-3">
                  <p className="overline text-[0.65rem] text-white/70">Progress</p>
                  <h4 className="font-display font-bold text-sm mb-2">2030 target</h4>
                  <div className="h-[96px] -mx-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={progressData}>
                        <XAxis dataKey="t" hide />
                        <YAxis hide domain={[420, 670]} />
                        <ReferenceLine y={436} stroke="#FDE047" strokeDasharray="4 4" />
                        <Line type="monotone" dataKey="v" stroke="#FDE047" strokeWidth={2.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="font-mono text-xs text-white/60">to target</span>
                    <span className="font-display font-bold text-lg text-[#FDE047]">−43%</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="overline mt-4 text-[#6B7280]" key={tick}>
              Source: DCCEEW; Open Electricity; FCAI
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatBlock({ value, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-display font-black text-3xl md:text-4xl tracking-tighter">{value}</div>
      <div className="text-xs md:text-sm text-[#6B7280] mt-1">{label}</div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-3 h-[2px]" style={{ background: color }} />
      <span className="font-mono text-[0.65rem] text-[#4B5563]">{label}</span>
    </div>
  );
}
